/**
 * The EpubLibraryDB schema: store map + the versioned migration registry.
 *
 * FORMAT SURFACE (C1): everything in this module is persisted-format
 * surface; bumping DB_VERSION is governed by master plan §4 rule 4. The v25
 * step (the registry's first entry) does:
 *
 *  1. **Straggler guard (snapshot-before-delete):** before the
 *     legacy-store deletion loop runs, any surviving v17/v18 USER-DATA
 *     store has its rows serialized into
 *     `app_metadata['legacy-recovery-v25']` (size-capped, binaries elided),
 *     so a returning pre-Yjs user's data is recoverable via
 *     support/diagnostics rather than silently destroyed.
 *  2. **`app_metadata` as the typed schema-evolution envelope:**
 *     `schemaHistory` appended on every upgrade, plus the recovery record
 *     (envelope schemas in rows/app.ts).
 *  3. **`cache_audio_blobs.by_lastAccessed` index** for the LRU eviction
 *     job; the post-open idle `size` backfill lives in repos/audioCache.
 *  4. `sync_log` untouched (a dead store with no readers or writers, kept
 *     frozen).
 *
 * Reversibility note: v25 is additive (a snapshot, an index, metadata) —
 * but IDB versions are monotonic, so a v24 build cannot open a v25 DB. The
 * straggler path is therefore recoverable rather than destructive.
 *
 * IDB only stores (user data lives in the Yjs CRDT):
 * - STATIC: immutable book content (manifests, blobs, structure)
 * - CACHE: ephemeral/regenerable data (render metrics, audio, TTS prep,
 *   search/embedding corpora, Drive previews)
 * - APP: sync checkpoints, flight snapshots, the frozen sync_log, and the
 *   schema-evolution envelope
 */
import type { DBSchema, IDBPDatabase, IDBPTransaction, StoreNames } from 'idb';
// rows/ is the source of truth for persisted shapes: every store's
// value type is its row type. The row types are supersets of the ~types
// domain interfaces that include persisted reality the interfaces elide —
// e.g. cache_audio_blobs' `size` + legacy `alignmentData` fields, and the
// ArrayBuffer state of binary fields the interfaces call Blob (WebKit's IDB
// cannot structured-clone Blob; ingest normalizes).
import type {
  StaticManifestRow,
  StaticResourceRow,
  StaticStructureRow,
} from './rows/static';
import type {
  CacheAudioBlobRow,
  CacheDrivePreviewRow,
  CacheEmbedJobsRow,
  CacheEmbeddingsRow,
  CacheRenderMetricsRow,
  CacheSearchTextRow,
  CacheSessionStateRow,
  CacheTtsPreparationRow,
  TableImageRow,
  CacheQueryEmbeddingsRow,
} from './rows/cache';
import type {
  AppMetadataValue,
  FlightSnapshotRow,
  LegacyRecoveryRecord,
  LegacyRecoveryStoreCapture,
  SchemaHistoryEntry,
  SyncCheckpointRow,
  SyncLogEntryRow,
} from './rows/app';
import { APP_METADATA_KEYS } from './rows/app';
import { createLogger } from '@lib/logger';

const logger = createLogger('DB');

/** The IndexedDB database name (shared with the SW read path, sw-contract.ts). */
export const DB_NAME = 'EpubLibraryDB';

/**
 * Current schema version. Bumping this is a user-data format change and is
 * governed by the one-in-flight rule (master plan §4 rule 4): append a step
 * to {@link MIGRATIONS}, never edit a released one. The steps after v25 are
 * all additive or clear-only and touch only cache-domain, device-local,
 * rebuildable stores (nothing user-authored); each `migrateToVNN` below
 * documents its own step: v26 `cache_search_text`, v27 `cache_embeddings` +
 * `cache_embed_jobs`, v28 a one-time clear of those two for the
 * embedding-model switch, v29/v30 `cache_query_embeddings`, v31
 * `cache_drive_previews`. The `sync_log` / SW legacy-`books` cover-fallback
 * cleanup has not shipped in any bump yet.
 */
export const DB_VERSION = 31;

/**
 * Interface defining the schema for the IndexedDB database.
 */
export interface EpubLibraryDB extends DBSchema {
  // --- DOMAIN 1: STATIC (Immutable Book Content) ---
  static_manifests: {
    key: string;
    value: StaticManifestRow;
  };
  static_resources: {
    key: string;
    value: StaticResourceRow;
  };
  static_structure: {
    key: string;
    value: StaticStructureRow;
  };

  // --- DOMAIN 2: CACHE (Ephemeral, Regenerable) ---
  cache_table_images: {
    key: string;
    value: TableImageRow;
    indexes: {
      by_bookId: string;
    };
  };
  cache_render_metrics: {
    key: string;
    value: CacheRenderMetricsRow;
  };
  cache_audio_blobs: {
    key: string;
    value: CacheAudioBlobRow;
    indexes: {
      /** v25: LRU eviction order. Older rows lacking `size` are
       *  still indexed — `lastAccessed` has always been required. */
      by_lastAccessed: number;
    };
  };
  cache_session_state: {
    key: string;
    value: CacheSessionStateRow;
  };
  cache_tts_preparation: {
    key: string;
    value: CacheTtsPreparationRow;
    indexes: {
      by_bookId: string;
    };
  };
  /** v26: per-book plain-text search corpus, written at import
   *  and deleted with the book. Rebuildable — absence triggers re-extraction. */
  cache_search_text: {
    key: string;
    value: CacheSearchTextRow;
  };
  /** v27: per-book embedding vectors that power semantic search (one row per
   *  book, keyPath bookId). Device-local, never synced; deleted with the book.
   *  The key IS the bookId, so no secondary index (mirrors cache_search_text). */
  cache_embeddings: {
    key: string;
    value: CacheEmbeddingsRow;
  };
  /** v27: per-section progress for the embedding-build job (keyPath bookId), so
   *  it can resume mid-book after an interruption. Dies with the book and its
   *  vectors. */
  cache_embed_jobs: {
    key: string;
    value: CacheEmbedJobsRow;
  };
  /** v29: persistent query embedding vectors cache (keyPath key). */
  cache_query_embeddings: {
    key: string;
    value: CacheQueryEmbeddingsRow;
  };
  /** v31: partial-fetch Drive EPUB previews (keyPath fileId) — metadata + cover
   *  extracted by ranged reads without a full download. Device-local, never
   *  synced; LRU-evicted via by_lastAccessed. Includes negative-cache rows
   *  (status:'unextractable'). */
  cache_drive_previews: {
    key: string;
    value: CacheDrivePreviewRow;
    indexes: {
      by_lastAccessed: number;
    };
  };

  // --- DOMAIN 3: APP (Sync Infrastructure + Schema Evolution) ---
  checkpoints: {
    key: number;
    value: SyncCheckpointRow;
    indexes: {
      by_timestamp: number;
    };
  };
  sync_log: {
    key: number;
    value: SyncLogEntryRow;
    indexes: {
      by_timestamp: number;
    };
  };
  app_metadata: {
    key: string;
    value: AppMetadataValue;
  };
  flight_snapshots: {
    key: string;
    value: FlightSnapshotRow;
  };
}

type UpgradeTransaction = IDBPTransaction<
  EpubLibraryDB,
  ArrayLike<StoreNames<EpubLibraryDB>>,
  'versionchange'
>;

/**
 * One step of the versioned migration registry. Steps are append-only:
 * a released step's body is part of the persisted format and must never be
 * edited — a later bug gets a later step.
 */
export interface IdbMigration {
  readonly toVersion: number;
  /**
   * Runs inside the versionchange transaction; may await `tx` operations
   * only (anything else lets the transaction auto-commit underneath you).
   */
  migrate(
    db: IDBPDatabase<EpubLibraryDB>,
    tx: UpgradeTransaction,
    oldVersion: number,
  ): Promise<void> | void;
}

/**
 * Stores deleted by the post-Yjs migration. Two generations: the v17
 * monolith layout, and the v18 `user_*` stores whose contents moved into
 * the Yjs CRDT. Frozen — entries are never removed (a straggler may carry
 * any of them).
 */
const DEPRECATED_STORES = [
  // Legacy v17 stores
  'books', 'book_sources', 'book_states', 'files',
  'annotations', 'lexicon', 'sections', 'content_analysis',
  'reading_history', 'reading_list', 'tts_queue', 'tts_position',
  'tts_cache', 'locations', 'tts_content', 'table_images',
  // v18 user stores (now in Yjs)
  'user_inventory', 'user_reading_list', 'user_progress',
  'user_annotations', 'user_overrides', 'user_journey', 'user_ai_inference',
] as const;

/**
 * The USER-DATA subset of {@link DEPRECATED_STORES} the v25 straggler guard
 * snapshots before deletion: stores holding data the user created (library
 * inventory, positions, annotations, vocabulary, history) rather than
 * regenerable derived content (files/sections/tts_* caches are rebuilt from
 * the EPUB; capturing multi-MB binaries would blow the size cap for zero
 * recovery value — binary fields are elided even in captured stores).
 */
const LEGACY_USER_DATA_STORES = [
  'books', 'book_states', 'annotations', 'lexicon',
  'reading_history', 'reading_list',
  'user_inventory', 'user_reading_list', 'user_progress',
  'user_annotations', 'user_overrides', 'user_journey', 'user_ai_inference',
] as const;

/**
 * Soft budget for the recovery snapshot, measured in serialized UTF-16 code
 * units (≈ bytes for the ASCII-dominant JSON involved). Keeps the upgrade
 * transaction's memory bounded; what does not fit marks `truncated`.
 */
export const LEGACY_RECOVERY_SIZE_CAP_BYTES = 8 * 1024 * 1024;

/**
 * JSON-serialize one legacy row with binary fields elided to descriptors —
 * structured-clone values JSON.stringify would corrupt (ArrayBuffer → `{}`)
 * or that would bloat the snapshot are replaced by
 * `{ __binary, byteLength }` markers.
 */
function serializeLegacyRow(row: unknown): string {
  return JSON.stringify(row, (_key, value: unknown) => {
    if (value instanceof ArrayBuffer) {
      return { __binary: 'ArrayBuffer', byteLength: value.byteLength };
    }
    if (ArrayBuffer.isView(value)) {
      return { __binary: value.constructor.name, byteLength: value.byteLength };
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      return { __binary: 'Blob', byteLength: value.size, type: value.type };
    }
    return value;
  });
}

/**
 * The v25 straggler guard. Serializes every surviving legacy
 * user-data store into `app_metadata['legacy-recovery-v25']` BEFORE the
 * deletion loop runs (never reorder: validation/snapshot first, destruction
 * second). No surviving stores → no record.
 */
async function captureLegacyUserData(
  db: IDBPDatabase<EpubLibraryDB>,
  tx: UpgradeTransaction,
  oldVersion: number,
): Promise<void> {
  const surviving = LEGACY_USER_DATA_STORES.filter((name) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    db.objectStoreNames.contains(name as any),
  );
  if (surviving.length === 0) return;

  logger.warn(
    `v25 straggler guard: capturing ${surviving.length} legacy user-data store(s) before deletion: ${surviving.join(', ')}`,
  );

  let budget = LEGACY_RECOVERY_SIZE_CAP_BYTES;
  let truncated = false;
  const stores: LegacyRecoveryStoreCapture[] = [];

  for (const name of surviving) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const store = tx.objectStore(name as any);
    const rowCount = await store.count();
    const serialized: string[] = [];

    let cursor = await store.openCursor();
    while (cursor) {
      if (budget <= 0) {
        truncated = true;
        break;
      }
      const json = serializeLegacyRow(cursor.value);
      if (json.length > budget) {
        truncated = true;
        break;
      }
      serialized.push(json);
      budget -= json.length;
      cursor = await cursor.continue();
    }

    stores.push({
      store: name,
      rowCount,
      capturedCount: serialized.length,
      rowsJSON: `[${serialized.join(',')}]`,
    });
  }

  const record: LegacyRecoveryRecord = {
    capturedAt: Date.now(),
    fromVersion: oldVersion,
    truncated,
    stores,
  };
  tx.objectStore('app_metadata').put(record, APP_METADATA_KEYS.legacyRecoveryV25);
}

/** The legacy-store deletion loop. */
function deleteDeprecatedStores(db: IDBPDatabase<EpubLibraryDB>): void {
  for (const store of DEPRECATED_STORES) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (db.objectStoreNames.contains(store as any)) {
      logger.info(`Deleting deprecated store: ${store}`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      db.deleteObjectStore(store as any);
    }
  }
}

/** The v25 step: straggler guard → deletion loop → LRU index. */
async function migrateToV25(
  db: IDBPDatabase<EpubLibraryDB>,
  tx: UpgradeTransaction,
  oldVersion: number,
): Promise<void> {
  // 1. Snapshot-before-delete: NEVER reorder these two.
  await captureLegacyUserData(db, tx, oldVersion);
  deleteDeprecatedStores(db);

  // 2. The LRU eviction index. The companion
  //    `size` backfill is a post-open idle job (repos/audioCache), not part
  //    of the upgrade transaction.
  const audioBlobs = tx.objectStore('cache_audio_blobs');
  if (!audioBlobs.indexNames.contains('by_lastAccessed')) {
    audioBlobs.createIndex('by_lastAccessed', 'lastAccessed');
  }
}

/**
 * The v26 step: create the EMPTY `cache_search_text` store —
 * the persisted per-book search corpus written at import and lazily on
 * first search. Purely additive: no existing data is read or moved.
 * Cache-domain and rebuildable, so the rollback story is trivial (the store
 * is regenerated from the EPUB if ever lost).
 */
function migrateToV26(db: IDBPDatabase<EpubLibraryDB>): void {
  if (!db.objectStoreNames.contains('cache_search_text')) {
    db.createObjectStore('cache_search_text', { keyPath: 'bookId' });
  }
}

/**
 * The v27 step: create the EMPTY `cache_embeddings` + `cache_embed_jobs`
 * stores — the per-book semantic-search vectors and the embedding-build job
 * progress, both keyed by bookId. Purely additive: no existing data is read or
 * moved, and each create is guarded by `contains()`. Cache-domain,
 * device-local and rebuildable, so the rollback story is trivial (the stores
 * are re-embedded if ever lost).
 */
function migrateToV27(db: IDBPDatabase<EpubLibraryDB>): void {
  if (!db.objectStoreNames.contains('cache_embeddings')) {
    db.createObjectStore('cache_embeddings', { keyPath: 'bookId' });
  }
  if (!db.objectStoreNames.contains('cache_embed_jobs')) {
    db.createObjectStore('cache_embed_jobs', { keyPath: 'bookId' });
  }
}

/**
 * The v28 step (embedding-model switch): CLEAR the `cache_embeddings` +
 * `cache_embed_jobs` stores once. The default embedding model moved from
 * `gemini-embedding-001` to `gemini-embedding-2` (useGenAIStore persist v2);
 * the two embedding spaces are incompatible, so a stored `-001` vector can
 * never be compared against a `-2` query (semanticRank skips a stamp mismatch)
 * and the indexer re-embeds the whole book anyway. Rather than leave the stale
 * vectors occupying disk until each book is next opened, clear both stores here
 * so the bytes are reclaimed at upgrade time; every book re-embeds under the new
 * model on its next index pass. Cache-domain, device-local and rebuildable, so
 * the clear is non-destructive (nothing user-authored). Empty/fresh installs
 * clear nothing. Each store is guarded by `contains()` (baseline creates both
 * before this step runs, so the guard is belt-and-suspenders).
 */
async function migrateToV28(
  db: IDBPDatabase<EpubLibraryDB>,
  tx: UpgradeTransaction,
): Promise<void> {
  for (const store of ['cache_embeddings', 'cache_embed_jobs'] as const) {
    if (db.objectStoreNames.contains(store)) {
      await tx.objectStore(store).clear();
    }
  }
}

/**
 * The v29 step: create the EMPTY `cache_query_embeddings` store.
 * Cache-domain, device-local, rebuildable.
 */
function migrateToV29(db: IDBPDatabase<EpubLibraryDB>): void {
  if (!db.objectStoreNames.contains('cache_query_embeddings')) {
    db.createObjectStore('cache_query_embeddings', { keyPath: 'key' });
  }
}

/**
 * The v30 step: additive step to force upgrade and create the EMPTY `cache_query_embeddings`
 * store if it was missed in any intermediate state.
 */
function migrateToV30(db: IDBPDatabase<EpubLibraryDB>): void {
  if (!db.objectStoreNames.contains('cache_query_embeddings')) {
    db.createObjectStore('cache_query_embeddings', { keyPath: 'key' });
  }
}

/**
 * The v31 step: create the EMPTY `cache_drive_previews` store (keyPath fileId)
 * plus its `by_lastAccessed` LRU index. Purely additive: no existing data is
 * read or moved, guarded by `contains()`. Cache-domain, device-local and
 * rebuildable — on an older build its absence just means Drive previews are
 * re-fetched, and nothing user-authored is touched.
 */
function migrateToV31(
  db: IDBPDatabase<EpubLibraryDB>,
  tx: UpgradeTransaction,
): void {
  const store = db.objectStoreNames.contains('cache_drive_previews')
    ? tx.objectStore('cache_drive_previews')
    : db.createObjectStore('cache_drive_previews', { keyPath: 'fileId' });
  if (!store.indexNames.contains('by_lastAccessed')) {
    store.createIndex('by_lastAccessed', 'lastAccessedAt');
  }
}

/**
 * The versioned migration registry. APPEND-ONLY: released steps are
 * persisted-format surface (migrations.test.ts runs them against committed
 * v18/v24 fixtures); a later fix is a later step, never an edit. Ordered
 * ascending; the final entry's `toVersion` must equal {@link DB_VERSION}
 * (pinned by the M suite).
 */
export const MIGRATIONS: readonly IdbMigration[] = [
  { toVersion: 25, migrate: migrateToV25 },
  { toVersion: 26, migrate: migrateToV26 },
  { toVersion: 27, migrate: migrateToV27 },
  { toVersion: 28, migrate: migrateToV28 },
  { toVersion: 29, migrate: migrateToV29 },
  { toVersion: 30, migrate: migrateToV30 },
  { toVersion: 31, migrate: migrateToV31 },
];

/**
 * Step 0 — the unversioned baseline, so any pre-24 straggler still
 * converges: create-if-missing for every current store. Runs on EVERY upgrade
 * before the versioned steps.
 */
function ensureBaselineStores(
  db: IDBPDatabase<EpubLibraryDB>,
  transaction: UpgradeTransaction,
): void {
  // Helper to create store if it doesn't exist
  const createStore = (name: string, options?: IDBObjectStoreParameters) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!db.objectStoreNames.contains(name as any)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return db.createObjectStore(name as any, options);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return transaction.objectStore(name as any);
  };

  // --- Create Active Stores ---

  // Static Domain
  createStore('static_manifests', { keyPath: 'bookId' });
  createStore('static_resources', { keyPath: 'bookId' });
  createStore('static_structure', { keyPath: 'bookId' });

  // Cache Domain
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableImages = createStore('cache_table_images', { keyPath: 'id' }) as any;
  if (!tableImages.indexNames.contains('by_bookId')) {
    tableImages.createIndex('by_bookId', 'bookId');
  }

  createStore('cache_render_metrics', { keyPath: 'bookId' });
  createStore('cache_audio_blobs', { keyPath: 'key' });
  createStore('cache_session_state', { keyPath: 'bookId' });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ttsPrep = createStore('cache_tts_preparation', { keyPath: 'id' }) as any;
  if (!ttsPrep.indexNames.contains('by_bookId')) {
    ttsPrep.createIndex('by_bookId', 'bookId');
  }

  // v27: the semantic-search embedding stores. KeyPath IS bookId, so no
  // secondary index (mirrors cache_search_text/cache_render_metrics).
  createStore('cache_embeddings', { keyPath: 'bookId' });
  createStore('cache_embed_jobs', { keyPath: 'bookId' });

  // v29: the query embedding vector cache. KeyPath IS key.
  createStore('cache_query_embeddings', { keyPath: 'key' });

  // v31: partial-fetch Drive previews. KeyPath IS fileId; the by_lastAccessed
  // LRU index is created by migrateToV31 (which runs on fresh installs too,
  // since oldVersion 0 < 31), mirroring how cache_audio_blobs' index landed.
  createStore('cache_drive_previews', { keyPath: 'fileId' });

  // App Domain
  if (!db.objectStoreNames.contains('checkpoints')) {
    const cp = db.createObjectStore('checkpoints', { keyPath: 'id', autoIncrement: true });
    cp.createIndex('by_timestamp', 'timestamp');
  }
  if (!db.objectStoreNames.contains('sync_log')) {
    const sl = db.createObjectStore('sync_log', { keyPath: 'id', autoIncrement: true });
    sl.createIndex('by_timestamp', 'timestamp');
  }
  if (!db.objectStoreNames.contains('app_metadata')) {
    db.createObjectStore('app_metadata');
  }
  if (!db.objectStoreNames.contains('flight_snapshots')) {
    db.createObjectStore('flight_snapshots', { keyPath: 'id' });
  }
}

/** Append `{ from, to, at }` to `schemaHistory` on every upgrade. */
async function appendSchemaHistory(
  tx: UpgradeTransaction,
  from: number,
  to: number,
): Promise<void> {
  const store = tx.objectStore('app_metadata');
  const existing = await store.get(APP_METADATA_KEYS.schemaHistory);
  const history: SchemaHistoryEntry[] = Array.isArray(existing) ? existing : [];
  history.push({ from, to, at: Date.now() });
  store.put(history, APP_METADATA_KEYS.schemaHistory);
}

/**
 * The upgrade callback: baseline (step 0) → versioned registry steps →
 * schemaHistory append. Passed to `openDB` by src/data/connection.ts.
 */
export async function upgradeSchema(
  db: IDBPDatabase<EpubLibraryDB>,
  oldVersion: number,
  newVersion: number | null,
  transaction: UpgradeTransaction,
): Promise<void> {
  const targetVersion = newVersion ?? DB_VERSION;
  logger.info(`Upgrading DB from v${oldVersion} to v${targetVersion}`);

  ensureBaselineStores(db, transaction);

  for (const step of MIGRATIONS) {
    if (oldVersion < step.toVersion) {
      await step.migrate(db, transaction, oldVersion);
    }
  }

  await appendSchemaHistory(transaction, oldVersion, targetVersion);
}
